import React, { Dispatch, Fragment, SetStateAction, useState } from 'react';
import { Button, FieldRow, Flex, Modal } from '.';

export type PromptModalFormButtonType = {
  bg?: string;
  text: string;
  cancel?: boolean;
  onCustomClickHandler?: () => string;
};

export type PromptModalFormProps = {
  open: boolean;
  heading: string;
  body?: string;
  id: string;
  promptId?: string;
  onDone: (promptId: string, button: string, cancelled: boolean, id: string) => void;
  buttons: PromptModalFormButtonType[] | string;
};

export const PromptModalFormPropsDefault: PromptModalFormProps = {
  open: false,
  heading: '',
  body: '',
  id: '',
  promptId: '',
  onDone: (promptId: string, button: string, cancelled: boolean, id: string) => {},
  buttons: [],
};

const PromptModalForm: React.FC<PromptModalFormProps> = props => {
  const { open, heading, body, id, buttons, onDone } = props;
  const promptId = props.promptId || '';

  const getPresetButtons = (preset: string): PromptModalFormButtonType[] => {
    if (preset === 'delete') {
      return [
        { text: 'Cancel', cancel: true },
        { text: 'Delete', bg: 'error' },
      ];
    }
    return [];
  };

  const buttonArray = Array.isArray(buttons) ? buttons : getPresetButtons(buttons);

  return (
    <Modal
      zIndex={2000}
      open={open}
      onClose={() => onDone(promptId, '', true, id)}
      heading={heading}
      body={body ? body : 'Are you sure?'}
      actions={
        <FieldRow justifyContent="flex-end">
          <Flex direction="row" size="1" justify="flex-end">
            <Flex direction="row" size="0.5" flexWrap="nowrap">
              {buttonArray.map((button, index, array) => {
                return (
                  <Button
                    key={`button-${index}`}
                    bg={button.bg}
                    text={button.text}
                    type="button"
                    width="40px"
                    mr={index < array.length - 1 ? 3 : undefined}
                    onClick={() => {
                      if (button.onCustomClickHandler) {
                        const result = button.onCustomClickHandler();
                        if (result) {
                          const resultIndex = buttonArray.findIndex(b => b.text === result);
                          if (resultIndex >= 0) {
                            const resultButton = buttonArray[resultIndex];
                            onDone(promptId, resultButton.text, resultButton.cancel ? true : false, id);
                          }
                        }
                      } else {
                        onDone(promptId, button.text, button.cancel ? true : false, id);
                      }
                    }}
                  />
                );
              })}
            </Flex>
          </Flex>
        </FieldRow>
      }
    />
  );
};

export const usePromptModalForm = (): [PromptModalFormProps, Dispatch<SetStateAction<PromptModalFormProps>>] => {
  const [showPrompt, setShowPrompt] = useState<PromptModalFormProps>(PromptModalFormPropsDefault);
  return [showPrompt, setShowPrompt];
};

export default PromptModalForm;

export const usePromptModalForm2 = (): [
  React.FC,
  (props: {
    heading: string;
    body?: string;
    id: string;
    promptId?: string;
    onDone: (promptId: string, button: string, cancelled: boolean, id: string) => void;
    buttons: PromptModalFormButtonType[] | string;
  }) => void
] => {
  const [showModal, setShowModal] = useState<PromptModalFormProps>(PromptModalFormPropsDefault);

  const show = (props: {
    heading: string;
    body?: string;
    id: string;
    promptId?: string;
    onDone: (promptId: string, button: string, cancelled: boolean, id: string) => void;
    buttons: PromptModalFormButtonType[] | string;
  }) => {
    if (!showModal.open) {
      setShowModal({
        open: true,
        heading: props.heading,
        body: props.body,
        id: props.id,
        promptId: props.promptId,
        buttons: props.buttons,
        onDone: (promptId: string, button: string, cancelled: boolean, id: string) => {
          props.onDone(promptId, button, cancelled, id);
          setShowModal(PromptModalFormPropsDefault);
        },
      });
    }
  };

  return [
    () => {
      return <Fragment>{showModal.open && <PromptModalForm {...showModal} />}</Fragment>;
    },
    show,
  ];
};
